import { useEffect, useRef, useState } from 'react';
import {
  fetchPresentationMetadata,
  parseAdQuery,
  probeAudioDescribedMetadataAvailable,
  readGlobalPreferAd,
  readInitialPreferAd,
  subscribeToGlobalPreferAd,
} from '../utils/v2PresentationApi';

export function useV2PresentationLoad({
  internalId,
  bookId,
  elementId,
  locationSearch,
  onLoadStart,
  onPresentationLoaded,
  onFetchError,
}) {
  const [preferAudioDescribed, setPreferAudioDescribed] = useState(() =>
    readInitialPreferAd(locationSearch),
  );
  const [audioDescribedMetadataExists, setAudioDescribedMetadataExists] =
    useState(false);

  const presentationLoadRefs = useRef({});
  const onLoadStartRef = useRef(onLoadStart);
  const onPresentationLoadedRef = useRef(onPresentationLoaded);
  const onFetchErrorRef = useRef(onFetchError);
  onLoadStartRef.current = onLoadStart;
  onPresentationLoadedRef.current = onPresentationLoaded;
  onFetchErrorRef.current = onFetchError;

  const lastInternalIdRef = useRef(internalId);
  if (lastInternalIdRef.current !== internalId) {
    lastInternalIdRef.current = internalId;
    const fromQuery = parseAdQuery(locationSearch);
    setPreferAudioDescribed(
      fromQuery !== null ? fromQuery : readGlobalPreferAd(),
    );
  }

  useEffect(() => {
    const fromQuery = parseAdQuery(locationSearch);
    if (fromQuery !== null) {
      setPreferAudioDescribed(fromQuery);
    }
  }, [locationSearch]);

  useEffect(() => subscribeToGlobalPreferAd(setPreferAudioDescribed), []);

  useEffect(() => {
    if (!internalId) return;

    const loadKey = `${internalId}|${preferAudioDescribed}`;
    if (presentationLoadRefs.current.loadKey === loadKey) {
      presentationLoadRefs.current.bookId = bookId;
      presentationLoadRefs.current.elementId = elementId;
      return;
    }
    presentationLoadRefs.current = {
      loadKey,
      bookId,
      elementId,
      internalId,
    };

    let cancelled = false;
    setAudioDescribedMetadataExists(false);
    onLoadStartRef.current();

    probeAudioDescribedMetadataAvailable(internalId)
      .then((adMetadataExists) => {
        if (cancelled) return null;
        const preferAdForFetch = preferAudioDescribed && adMetadataExists;
        return fetchPresentationMetadata(internalId, preferAdForFetch).then(
          (presentationResult) => ({ adMetadataExists, presentationResult }),
        );
      })
      .then((combined) => {
        if (cancelled || !combined) return;
        const { adMetadataExists, presentationResult } = combined;
        setAudioDescribedMetadataExists(adMetadataExists);
        onPresentationLoadedRef.current({ presentationResult });
      })
      .catch(() => {
        if (!cancelled) {
          setAudioDescribedMetadataExists(false);
          onFetchErrorRef.current();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bookId, elementId, internalId, preferAudioDescribed]);

  return {
    preferAudioDescribed,
    setPreferAudioDescribed,
    audioDescribedMetadataExists,
    presentationLoadRefs,
  };
}
